import React, { useEffect, useState } from 'react';
import { Compass, ArrowRight } from 'lucide-react';
import './SeriesFocusOfferCard.css';

/**
 * Series Discovery Focus session offer (#708 / #725 / #756 / #774).
 *
 * Light in-chat choice after some depth. Easy to decline. Not a gate.
 * Accept opens the existing Create New Session Focus path at series
 * altitude. This card does not mint a session or a card.
 * Do not show this card on the same stack as a book-card offer.
 * The backend does not stamp this card on a book-idea ask (#774).
 *
 * Decline is session-scoped: Not now and × hide every mounted copy in
 * this Discovery notebook and persist so reload does not nag.
 */

export const SERIES_FOCUS_OFFER_HINT =
  'Want to sit with one thread in Focus? Easy to skip — or keep going here.';

export const SERIES_FOCUS_OFFER_ACCEPT_LABEL = 'Start a Focus session';

const declineListeners = new Set();

export function declineStorageKey(sessionId) {
  return `brainstormy.seriesFocusOffer.declined.${sessionId}`;
}

export function subscribeSeriesFocusOfferDecline(listener) {
  declineListeners.add(listener);
  return () => declineListeners.delete(listener);
}

export function isSeriesFocusOfferDeclined(sessionId) {
  if (!sessionId) return false;
  try {
    return window.localStorage.getItem(declineStorageKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

export function markSeriesFocusOfferDeclined(sessionId) {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(declineStorageKey(sessionId), '1');
  } catch {
    /* private mode / quota — hide in this page either way */
  }
  declineListeners.forEach((listener) => {
    listener(sessionId);
  });
}

export function shouldShowSeriesFocusOffer(offer, sessionId, { bookCardShowing } = {}) {
  if (bookCardShowing) return false;
  if (!offer?.offered) return false;
  if (isSeriesFocusOfferDeclined(sessionId)) return false;
  return true;
}

export default function SeriesFocusOfferCard({
  sessionId,
  onAccept,
  onDecline,
}) {
  const [hidden, setHidden] = useState(() => isSeriesFocusOfferDeclined(sessionId));

  useEffect(() => {
    if (isSeriesFocusOfferDeclined(sessionId)) {
      setHidden(true);
    }
    return subscribeSeriesFocusOfferDecline((id) => {
      if (id === sessionId) setHidden(true);
    });
  }, [sessionId]);

  if (hidden) return null;

  const handleDecline = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    markSeriesFocusOfferDeclined(sessionId);
    setHidden(true);
    onDecline?.();
  };

  return (
    <div className="series-focus-offer" role="group" aria-label="Optional Focus session">
      <div className="series-focus-offer__lead">
        <span className="series-focus-offer__hint">{SERIES_FOCUS_OFFER_HINT}</span>
        <button
          type="button"
          className="series-focus-offer__dismiss"
          onClick={handleDecline}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
      <div className="series-focus-offer__doors">
        <button
          type="button"
          className="series-focus-offer__door"
          onClick={() => onAccept?.()}
        >
          <Compass className="series-focus-offer__icon" size={15} />
          <span className="series-focus-offer__label">{SERIES_FOCUS_OFFER_ACCEPT_LABEL}</span>
          <ArrowRight className="series-focus-offer__arrow" size={14} />
        </button>
        <button
          type="button"
          className="series-focus-offer__skip"
          onClick={handleDecline}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
